import {
	brandSchema,
	type BrandsResponse,
	type DynamicInputData,
	type StaticInputData,
} from '@laam/cms-shared';
import { validatorFactoryArray } from '@laam/lib/validator';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';

const validator = validatorFactoryArray<BrandsResponse>(brandSchema);

export async function fetchBrands(
	filters: DynamicInputData | StaticInputData,
): Promise<BrandsResponse> {
	let body: unknown = {};
	if ('items' in filters) {
		if (filters.items.length > 0) {
			body = {
				id: filters.items.map((item) => item.id).join(','),
				sort_by: 'NO_RANK',
			};
		} else {
			return [];
		}
	} else if ('ids' in filters) {
		if (filters.ids.length > 0) {
			body = {
				id: filters.ids.join(','),
				sort_by: 'NO_RANK',
			};
		} else {
			return [];
		}
	} else {
		const { selectedPin, selectedCategory, ranking, timePeriod, ...rest } =
			filters;
		body = {
			sort_by: ranking,
			...(timePeriod && { period: timePeriod }),
			...(selectedPin && { pinned_brands: selectedPin }),
			...(selectedCategory && { node_id: selectedCategory }),
			...rest,
		};
	}
	const api = createApiInstance();
	const response = await api.get(
		useRuntimeConfig().public.apiBaseUrl + '/v1/brands',
		{
			params: body,
		},
	);

	if (response.status === 404) {
		return [];
	}

	if (response.status === 200 && Array.isArray(response.data)) {
		try {
			const body = validator.verify(response.data);
			return body;
		} catch (e) {
			console.log('ERROR', e);
		}
	}

	throw new Error('Invalid response from server');
}

export const useBrands = (opts: {
	filters: Ref<DynamicInputData | StaticInputData>;
	enabled: Ref<boolean>;
}) => {
	const filterString = computed(() => {
		return JSON.stringify(opts.filters.value);
	});
	const { data, isLoading, suspense, error, isFetching, status } = useQuery({
		queryKey: ['brands', filterString],
		queryFn: () => fetchBrands(opts.filters.value),
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		enabled: opts.enabled,
		staleTime: Infinity,
		placeholderData: keepPreviousData,
	});
	return {
		data,
		isLoading,
		waitForBrands: suspense,
		error,
		brandsLoading: isFetching,
		status,
	};
};
